import React, {createContext, useCallback, useContext, useRef, useState} from 'react';
import {ConfirmDialog, ConfirmOptions} from '../components/UI/ConfirmDialog';

/**
 * `useConfirm()` - hỏi lại trước khi làm, bằng giao diện của app.
 * ---------------------------------------------------------------------------
 * Dùng y như `window.confirm`, chỉ thêm chữ `await`:
 *
 *     if (!await confirm({title: 'Xoá bản ghi tháng 3/2026?', tone: 'danger'})) return;
 *
 * Giữ nguyên hình dạng "hỏi rồi thoát sớm" là có chủ ý. Cách còn lại - mỗi chỗ
 * tự nuôi một state `showConfirm` rồi tách hàm xử lý ra làm hai nửa - sẽ bẻ đôi
 * hai mươi mấy hàm đang đọc trôi chảy từ trên xuống, và nửa sau (nửa thật sự
 * ghi dữ liệu) mất luôn ngữ cảnh của nửa đầu.
 *
 * Provider đặt ở App.tsx, trên cả Router: HomePage và các trang công khai nằm
 * ngoài AppLayout nhưng vẫn cần hỏi (huỷ yêu cầu nhận phòng).
 *
 * Xem components/UI/ConfirmDialog.tsx cho các trường của `options` và vì sao
 * chúng tồn tại.
 */

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined);

interface PendingRequest {
    /** Số thứ tự của lượt hỏi - xem chỗ dùng làm `key` bên dưới */
    id: number;
    options: ConfirmOptions;
    resolve: (answer: boolean) => void;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [pending, setPending] = useState<PendingRequest | null>(null);

    /* Lời hứa của lượt hỏi đang mở, đọc qua ref chứ không qua state: `settle`
       chạy trong handler của lớp phủ, mà state lúc đó có thể đã là lượt khác. */
    const pendingRef = useRef<PendingRequest | null>(null);
    const nextId = useRef(0);

    const confirm = useCallback<ConfirmFn>(options => new Promise<boolean>(resolve => {
        /* Đang hỏi dở mà có câu hỏi mới thì câu cũ tính là "không" - phía gọi
           coi đó là người dùng bấm Hủy, tức là không ghi gì cả. Chọn hướng an
           toàn vì tình huống này chỉ xảy ra khi hai đường cùng gọi confirm (vd
           phím Esc chạm vào cả lớp phủ lẫn hộp thoại), chứ không phải ý người
           dùng muốn trả lời hai câu. */
        pendingRef.current?.resolve(false);

        nextId.current += 1;
        const request: PendingRequest = {id: nextId.current, options, resolve};
        pendingRef.current = request;
        setPending(request);
    }), []);

    const settle = useCallback((answer: boolean) => {
        pendingRef.current?.resolve(answer);
        pendingRef.current = null;
        setPending(null);
    }, []);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {pending && (
                <ConfirmDialog
                    /* Dựng lại từ đầu cho mỗi lượt hỏi: hộp thoại tự đưa con trỏ
                       vào đúng nút khi mở, mà giữ nguyên instance thì lượt sau
                       không chạy lại bước đó. */
                    key={pending.id}
                    options={pending.options}
                    onConfirm={() => settle(true)}
                    onCancel={() => settle(false)}
                />
            )}
        </ConfirmContext.Provider>
    );
};

export const useConfirm = (): ConfirmFn => {
    const confirm = useContext(ConfirmContext);
    if (!confirm) throw new Error('useConfirm must be used within a ConfirmProvider');
    return confirm;
};
